import React, { useEffect, useState } from "react";
import { Globe2, Loader2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/Card";
import { Picker } from "../../components/ui/Picker";
import { ToggleSwitch } from "../../components/ui/ToggleSwitch";
import { SettingsBanner } from "../../components/ui/SettingsBanner";
import { citiesApi } from "../../utils/api";
import { languageName, sourceLabel } from "../../utils/helpers";

// A picked list, not a typed one: the zone decides which calendar day the API treats as past
// for every event in the city, so a typo would silently put it back on the UTC day this
// setting exists to leave. The API rejects an unknown zone too — this just means nobody has
// to find out that way. `Intl.supportedValuesOf` is the browser's own IANA list (no
// dependency, always current); an older browser without it falls back to whatever the city
// already has, so the row still renders and still saves.
const TIMEZONES = (() => {
  try {
    return Intl.supportedValuesOf("timeZone");
  } catch {
    return [];
  }
})();
const TIMEZONE_SET = new Set(TIMEZONES);
// ~450 <option> elements, built once for the page rather than rebuilt per city per render.
const TIMEZONE_OPTIONS = TIMEZONES.map((tz) => (
  <option key={tz} value={tz}>
    {tz}
  </option>
));

export default function CitySourceToggles() {
  const [cities, setCities] = useState([]);
  const [supportedLocales, setSupportedLocales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [savingKey, setSavingKey] = useState(null); // `${city}:${source}` in flight
  // The value the admin just picked, held until the PATCH answers. Without it a select is
  // controlled purely by the city's stored value, which only changes on success — so the
  // re-render that marks the row in flight snaps the picker back for the length of the
  // request, which reads as "it didn't take" and invites a second, racing PATCH.
  // Keyed by the same `${city}:${field}` as `savingKey`.
  const [pending, setPending] = useState({});

  useEffect(() => {
    citiesApi
      .getSources()
      .then(({ cities: loaded, supportedLocales: locales }) => {
        setCities(loaded);
        setSupportedLocales(locales);
      })
      .catch((err) => {
        console.error("Failed to load city source settings", err);
        setError("Failed to load city source settings");
      })
      .finally(() => setLoading(false));
  }, []);

  // Every edit on this page is the same save: mark the row in flight, call one endpoint,
  // replace the city with whatever came back. The merge is the part that has to stay in step
  // with the response shape, so it lives here once rather than in each handler.
  const saveCity = async (cityName, key, call, errorMessage) => {
    setSavingKey(`${cityName}:${key}`);
    setError(null);
    try {
      const updated = await call();
      setCities((prev) => prev.map((c) => (c.city_name === cityName ? updated : c)));
    } catch {
      setError(errorMessage);
    } finally {
      setSavingKey((k) => (k === `${cityName}:${key}` ? null : k));
    }
  };

  const toggle = (cityName, source, next) =>
    saveCity(
      cityName,
      source,
      () => citiesApi.updateSources(cityName, { [source]: next }),
      `Failed to update ${sourceLabel(source)} for ${cityName}`
    );

  // Cleared only if it is still ours: pick twice before the first PATCH answers and the first
  // one's cleanup must not drop the second pick, or the picker snaps back mid-request — the
  // exact failure this state prevents. Other cities' and fields' picks are never touched.
  const savePicked = (cityName, field, value, call, errorMessage) => {
    const key = `${cityName}:${field}`;
    setPending((p) => ({ ...p, [key]: value }));
    return saveCity(cityName, field, call, errorMessage).finally(() =>
      setPending((p) => {
        if (p[key] !== value) return p;
        const { [key]: _done, ...rest } = p;
        return rest;
      })
    );
  };
  const pickedValue = (city, field, stored) => {
    const key = `${city.city_name}:${field}`;
    return (key in pending ? pending[key] : stored) || "";
  };

  const setTimezone = (cityName, tz) =>
    savePicked(
      cityName,
      "__timezone__",
      tz,
      () => citiesApi.updateTimezone(cityName, tz),
      `Failed to set the timezone for ${cityName}`
    );

  const setDefaultLanguage = (cityName, language) =>
    savePicked(
      cityName,
      "__language__",
      language,
      () => citiesApi.updateDefaultLanguage(cityName, language),
      `Failed to set the default language for ${cityName}`
    );

  const toggleActive = (cityName, next) =>
    saveCity(
      cityName,
      "__active__",
      () => citiesApi.updateCity(cityName, { active: next }),
      `Failed to ${next ? "activate" : "deactivate"} ${cityName}`
    );

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Globe2 className="h-5 w-5 text-muted-foreground" />
          <CardTitle>Event Sources Per City</CardTitle>
        </div>
        <CardDescription>
          Activate a city and enable or disable each ingestion source. Deactivating a city excludes it from
          ingestion and the public city list, and disables its source toggles. Only the sources available for a
          city are shown. The timezone is the day the city&apos;s own audience is living — it decides when an
          event stops being shown as upcoming. Without one the API falls back to the UTC day plus a day of
          grace, which keeps yesterday&apos;s events visible east of UTC. The default language is the one the
          city&apos;s listings are natively written in.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <SettingsBanner error={error} />
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />Loading...
          </div>
        ) : (
          <div className="space-y-6">
            {cities.map((city) => (
              <div key={city.city_name}>
                <ToggleSwitch
                  label={city.city_name}
                  description={city.active ? "Active — included in ingestion" : "Inactive — excluded from ingestion"}
                  checked={!!city.active}
                  disabled={savingKey === `${city.city_name}:__active__`}
                  onToggle={() => toggleActive(city.city_name, !city.active)}
                  trailing={
                    <div className="flex items-center gap-2">
                      <Picker
                        compact
                        className="w-48"
                        label={`Timezone for ${city.city_name}`}
                        value={pickedValue(city, "__timezone__", city.timezone)}
                        disabled={savingKey === `${city.city_name}:__timezone__`}
                        onChange={(e) => setTimezone(city.city_name, e.target.value)}
                      >
                        <option value="">No timezone (UTC day)</option>
                        {/* A zone stored before this list existed, or dropped from a later tzdata,
                            would otherwise render as the blank option and read as unset. */}
                        {city.timezone && !TIMEZONE_SET.has(city.timezone) ? (
                          <option value={city.timezone}>{city.timezone}</option>
                        ) : null}
                        {TIMEZONE_OPTIONS}
                      </Picker>
                      <Picker
                        compact
                        className="w-36"
                        label={`Default language for ${city.city_name}`}
                        value={pickedValue(city, "__language__", city.default_language)}
                        disabled={savingKey === `${city.city_name}:__language__`}
                        onChange={(e) => setDefaultLanguage(city.city_name, e.target.value)}
                      >
                        <option value="">No language</option>
                        {/* A code dropped from SUPPORTED_LOCALES since it was set would otherwise
                            render as the blank option and read as unset. */}
                        {city.default_language && !supportedLocales.includes(city.default_language) ? (
                          <option value={city.default_language}>{city.default_language}</option>
                        ) : null}
                        {supportedLocales.map((code) => (
                          <option key={code} value={code}>
                            {languageName(code)}
                          </option>
                        ))}
                      </Picker>
                    </div>
                  }
                />
                <div className="mt-3 space-y-3 pl-4">
                  {Object.keys(city.enabled_sources).length === 0 ? (
                    <div className="text-sm text-muted-foreground">No sources available</div>
                  ) : (
                    Object.keys(city.enabled_sources).map((source) => (
                      <ToggleSwitch
                        key={source}
                        label={sourceLabel(source)}
                        checked={!!city.enabled_sources[source]}
                        disabled={!city.active || savingKey === `${city.city_name}:${source}`}
                        onToggle={() => toggle(city.city_name, source, !city.enabled_sources[source])}
                      />
                    ))
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
